/**
 * API Implementation of IVocabularyService
 * All methods use real BE endpoints.
 */

import { clientApi } from "@/utils/api";
import { TApiRes } from "@/types/api";
import {
  TVocabularyCategory,
  TVocabularyWord,
  TVocabularyWordDTO,
  TVocabularyPracticeSession,
  TVocabularyQuizSession,
  TQuizSessionDTO,
  TQuizAnswerRequest,
  TQuizAnswerResponse,
  TVocabularyQuizResult,
  TQuizResultDTO,
} from "@/infrastructure/adapters/vocabulary/vocabulary.types";
import { isMasteredByCorrectCount, parseRecentResults } from "@/infrastructure/adapters/vocabulary/vocabulary.mastery";
import { IVocabularyService } from "@/infrastructure/services/vocabulary/vocabulary.service";

/** Answered-at-least-once threshold behind 覚えた単語 (the list filter and the summary count) */
const LEARNED_THRESHOLD = 2;

/**
 * Map API DTO to domain model.
 * `status` tracks 覚えた単語 via `learnedCount` — a separate axis from the ✓ (習得済み) badge,
 * which needs `MASTERED_CORRECT_THRESHOLD` correct answers.
 */
const mapToDomain = (dto: TVocabularyWordDTO): TVocabularyWord => ({
  id: String(dto.id),
  kanji: dto.word,
  furigana: dto.furigana ?? "",
  translation: dto.explanation,
  imageFile: dto.imageFile ?? null,
  imageUrl: dto.imageUrl ?? null,
  status: dto.learnedCount >= LEARNED_THRESHOLD ? "mastered" : "not_mastered",
  recentResults: parseRecentResults(dto.recentResults),
  masteryCorrectCount: dto.masteryCorrectCount ?? 0,
});

export const apiVocabularyService: IVocabularyService = {
  getCategories: async () => {
    const res = await clientApi.get<TApiRes<TVocabularyCategory[] | null>>("/learning/vocabulary-categories");
    return res.data.data;
  },

  getWords: async (category?: string) => {
    const res = await clientApi.get<TApiRes<TVocabularyWordDTO[]>>("/learning/vocabularies", {
      params: { ...(category && { category }) },
    });
    return res.data.data.map(mapToDomain);
  },

  getPracticeSession: async (limit: number, category?: string): Promise<TVocabularyPracticeSession> => {
    const res = await clientApi.get<TApiRes<TVocabularyWordDTO[]>>("/learning/vocabularies", {
      params: { limit, ...(category && { category }) },
    });
    const words = res.data.data.map(mapToDomain);
    return { words, totalCount: words.length };
  },

  updateProgress: async (vocabularyId: number): Promise<boolean> => {
    const res = await clientApi.patch<TApiRes<boolean>>(`/learning/vocabularies/${vocabularyId}/progress`);
    return res.data.data;
  },

  createQuizSession: async (limit: number, category?: string): Promise<TVocabularyQuizSession> => {
    const res = await clientApi.post<TApiRes<TQuizSessionDTO>>("/learning/vocabulary-quiz/sessions", {
      limit,
      ...(category && { category }),
    });
    const dto = res.data.data;
    return {
      quizSessionId: dto.quizSessionId,
      totalCount: dto.totalQuestions,
      questions: dto.questions.map((q) => ({
        sessionItemId: q.sessionItemId,
        word: {
          id: String(q.vocabularyId),
          kanji: q.word,
          furigana: q.furigana ?? "",
          translation: "",
          // Quiz hides word images (BE no longer sends them)
          imageFile: null,
          imageUrl: null,
          status: isMasteredByCorrectCount(q.masteryCorrectCount ?? 0) ? ("mastered" as const) : ("not_mastered" as const),
          recentResults: parseRecentResults(q.recentResults),
          masteryCorrectCount: q.masteryCorrectCount ?? 0,
        },
        options: q.choices.map((c) => ({ choiceId: c.choiceId, text: c.text })),
      })),
    };
  },

  submitAnswer: async (quizSessionId: string, req: TQuizAnswerRequest): Promise<TQuizAnswerResponse> => {
    const res = await clientApi.post<TApiRes<TQuizAnswerResponse>>(`/learning/vocabulary-quiz/sessions/${quizSessionId}/answers`, req);
    return res.data.data;
  },

  getQuizResult: async (quizSessionId: string): Promise<TVocabularyQuizResult> => {
    const res = await clientApi.get<TApiRes<TQuizResultDTO>>(`/learning/vocabulary-quiz/sessions/${quizSessionId}/result`);
    const dto = res.data.data;
    return {
      quizSessionId: dto.quizSessionId,
      correctCount: dto.correctCount,
      wrongCount: dto.wrongCount,
      totalQuestions: dto.totalQuestions,
      accuracyRate: dto.accuracyRate,
      items: dto.items.map((item) => ({
        questionId: item.questionId,
        displayOrder: item.displayOrder,
        word: item.word,
        furigana: item.furigana,
        isCorrect: item.isCorrect,
        selectedOptionId: item.selectedOptionId,
        correctOptionId: item.correctOptionId,
        selectedOptionText: item.selectedOptionText,
        correctOptionText: item.correctOptionText,
        options: item.options.map((o) => ({ optionId: o.optionId, text: o.text })),
      })),
    };
  },
};
